import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    Home, 
    BarChart2, 
    Plus, 
    User, 
    Repeat, 
    Handshake,
    Send 
} from 'lucide-react';

const MemberBottomNav = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Navigation Configuration
    const navItems = [
        { id: 'home', label: 'Home', icon: Home, path: '/member/dashboard' },
        { id: 'data', label: 'Data', icon: BarChart2, path: '/member/memberdata' },
        { id: 'add', label: 'Add', icon: Plus, path: null, isAction: true },
        { id: 'exchange', label: 'Exchange', icon: Repeat, path: '/member/memberdata' }, 
        { id: 'profile', label: 'Profile', icon: User, path: '/member/memberprofile' }
    ];

    const handleNavigation = (path) => {
        setIsMenuOpen(false);
        navigate(path);
    };

    return (
        <>
            {/* ==============================================
                1. ANIMATED OVERLAY MENU
            ============================================== */}
            <div 
                className={`fixed inset-0 z-50 flex justify-center items-end pb-28 pointer-events-none transition-all duration-300 ${isMenuOpen ? 'visible' : 'invisible'}`}
            >
                {/* Backdrop */}
                <div 
                    className={`absolute inset-0 bg-slate-900/60 backdrop-blur-[4px] transition-opacity duration-300 pointer-events-auto ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`}
                    onClick={() => setIsMenuOpen(false)}
                ></div>

                {/* Animated Menu Buttons Container */}
                <div className="relative w-full max-w-md flex justify-center items-end pointer-events-none mb-4">
                    
                    {/* 1. LEFT BUTTON: Business Exchange */}
                    <div 
                        className={`absolute bottom-0 transition-all duration-500 cubic-bezier(0.34, 1.56, 0.64, 1) pointer-events-auto flex flex-col items-center gap-2
                            ${isMenuOpen 
                                ? 'translate-x-[-100px] translate-y-[-15px] scale-100 opacity-100' // Moved further left, kept low
                                : 'translate-x-0 translate-y-[50px] scale-50 opacity-0'}
                        `}
                    >
                        <button
                            onClick={() => handleNavigation('/member/businessexchange')}
                            className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-xl shadow-blue-500/30 flex items-center justify-center border-2 border-white hover:scale-110 transition-transform"
                        >
                            <Repeat size={24} />
                        </button>
                        <span className="text-white text-[10px] font-bold bg-black/50 px-2 py-1 rounded-full backdrop-blur-md whitespace-nowrap">
                            Exchange
                        </span>
                    </div>

                    {/* 2. CENTER TOP BUTTON: Give Reference (Lowered Position) */}
                    <div 
                        className={`absolute bottom-0 transition-all duration-500 cubic-bezier(0.34, 1.56, 0.64, 1) pointer-events-auto flex flex-col items-center gap-2 delay-75
                            ${isMenuOpen 
                                ? 'translate-x-0 translate-y-[-75px] scale-100 opacity-100' // Lowered from -100px to -75px
                                : 'translate-x-0 translate-y-[50px] scale-50 opacity-0'}
                        `}
                    >
                        <button
                            onClick={() => handleNavigation('/member/givereference')}
                            className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-xl shadow-orange-500/30 flex items-center justify-center border-2 border-white hover:scale-110 transition-transform"
                        >
                            <Send size={28} className="ml-1" />
                        </button>
                        <span className="text-white text-[11px] font-bold bg-black/50 px-3 py-1 rounded-full backdrop-blur-md whitespace-nowrap">
                            Give Reference
                        </span>
                    </div>

                    {/* 3. RIGHT BUTTON: One to One Meeting */}
                    <div 
                        className={`absolute bottom-0 transition-all duration-500 cubic-bezier(0.34, 1.56, 0.64, 1) pointer-events-auto flex flex-col items-center gap-2 delay-100
                            ${isMenuOpen 
                                ? 'translate-x-[100px] translate-y-[-15px] scale-100 opacity-100' // Moved further right, kept low
                                : 'translate-x-0 translate-y-[50px] scale-50 opacity-0'}
                        `}
                    >
                        <button
                            onClick={() => handleNavigation('/member/onetoone')}
                            className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white shadow-xl shadow-green-500/30 flex items-center justify-center border-2 border-white hover:scale-110 transition-transform"
                        >
                            <Handshake size={24} />
                        </button>
                        <span className="text-white text-[10px] font-bold bg-black/50 px-2 py-1 rounded-full backdrop-blur-md whitespace-nowrap">
                            1-to-1
                        </span>
                    </div>

                </div>
            </div>

            {/* ==============================================
                2. BOTTOM NAVIGATION BAR
            ============================================== */}
            <div className="fixed bottom-0 left-0 w-full z-50 pointer-events-none md:pb-6">
                <div className="max-w-md mx-auto relative pointer-events-auto">
                    
                    {/* The Bar */}
                    <div className="bg-white h-[84px] rounded-t-[30px] md:rounded-[35px] shadow-[0_-5px_30px_rgba(0,0,0,0.08)] md:shadow-[0_10px_40px_rgba(0,0,0,0.15)] flex justify-between items-end px-4 pb-3 relative z-10 border-t border-blue-50 md:border md:border-slate-100">
                        
                        {navItems.map((item, index) => {
                            const isActive = location.pathname === item.path;
                            
                            // --- SPECIAL CENTER BUTTON (Add) ---
                            if (item.isAction) {
                                return (
                                    <div key={item.id} className="relative w-1/5 flex flex-col items-center justify-end h-full">
                                        {/* Floating Button Container */}
                                        <div className="absolute -top-6">
                                             <button
                                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                                className={`
                                                    w-16 h-16 rounded-full flex items-center justify-center 
                                                    shadow-[0_8px_20px_rgba(26,135,207,0.4)]
                                                    transition-all duration-300 transform cursor-pointer
                                                    border-[5px] border-[#F8FAFC]
                                                    ${isMenuOpen 
                                                        ? 'bg-slate-800 rotate-45 scale-105' 
                                                        : 'bg-gradient-to-tr from-[#1A87CF] to-[#4FB0F0] hover:-translate-y-1 hover:shadow-[0_12px_25px_rgba(26,135,207,0.5)]'
                                                    }
                                                `}
                                            >
                                                <Plus size={32} className="text-white" strokeWidth={3} />
                                            </button>
                                        </div>

                                        {/* "Add" Label Below */}
                                        <span className={`
                                            mt-10 text-[11px] font-medium transition-all duration-300
                                            ${isMenuOpen ? 'text-slate-800 font-bold' : 'text-slate-500'}
                                        `}>
                                            {isMenuOpen ? 'Close' : 'Add'}
                                        </span>
                                    </div>
                                );
                            }

                            // --- NORMAL NAV ITEMS ---
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleNavigation(item.path)}
                                    className="w-1/5 flex flex-col items-center justify-center gap-1 group pb-1 cursor-pointer"
                                >
                                    {/* Icon Box */}
                                    <div className={`
                                        p-1.5 rounded-xl transition-all duration-300
                                        ${isActive 
                                            ? 'text-[#1A87CF] -translate-y-1 bg-blue-50' 
                                            : 'text-slate-400 group-hover:text-slate-600 group-hover:bg-slate-50'
                                        }
                                    `}>
                                        <item.icon 
                                            size={24} 
                                            strokeWidth={isActive ? 2.5 : 2} 
                                        />
                                    </div>
                                    
                                    {/* Label */}
                                    <span className={`
                                        text-[11px] font-medium transition-all duration-300
                                        ${isActive 
                                            ? 'text-[#1A87CF] font-bold' 
                                            : 'text-slate-400 group-hover:text-slate-600'
                                        }
                                    `}>
                                        {item.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};

export default MemberBottomNav;